import { getRgbaColor, breakpoints, v2Colors, v2ButtonStyles } from '@web-for-marketing/react-ui';
import { typographyStyles } from '@web-for-marketing/react-ui';
import externalLinkWithBg from '@images/Blog/ExternalLinkWithBackground.svg';
import openInNewTab from '@images/Blog/OpenInNewTab.svg';

export const ckEditorHTMLStyles = {
    container: {
        marginBottom: '4rem',
        width: '100%',
    },
    articleStyles: {
        color: v2Colors.text.secondary,
        '& a[data-button]': {
            '&[data-variant="primary"]': v2ButtonStyles({ variant: 'primary' }),
            '&[data-variant="secondary"]': v2ButtonStyles({ variant: 'secondary' }),
            '&[data-variant="tertiary"]': v2ButtonStyles({ variant: 'tertiary' }),
            '&[data-variant="link"]': v2ButtonStyles({ variant: 'link' }),
            '&[data-variant="floating"]': v2ButtonStyles({ variant: 'floating' }),
            '&[data-variant="ghost"]': v2ButtonStyles({ variant: 'ghost' }),
        },
        '& a:not([data-button])': {
            transition: '200ms background-color ease',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            textDecoration: 'underline',
            color: v2Colors.core.innovation,
            wordBreak: 'break-word',
            padding: '0.5rem 0.2rem',
            '&:hover': {
                backgroundColor: `${getRgbaColor(v2Colors.core.deepSpace, 0.03)} !important`,
            },
            '&:focus': {
                outlineColor: v2Colors.core.innovation,
                backgroundColor: `${getRgbaColor(v2Colors.core.deepSpace, 0.06)} !important`,
            },
            '&:active': {
                outline: 'none',
                backgroundColor: `${getRgbaColor(v2Colors.core.deepSpace, 0.05)} !important`,
            },
            '&[target="_blank"]:not(figure *)::after': {
                content: '""',
                height: '0.8em',
                width: '0.8em',
                marginLeft: '0.6rem',
                verticalAlign: 'baseline',
                display: 'inline-block',
                backgroundImage: `url(${openInNewTab})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
            },
            '&:not([href*="www.geotab.com"]):not([href^="/"]):not(figure *)::after': {
                content: '""',
                height: '0.8em',
                width: '0.8em',
                marginLeft: '0.6rem',
                verticalAlign: 'baseline',
                display: 'inline-block',
                backgroundImage: `url(${openInNewTab})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
            },
        },
        '& quote': {
            fontSize: '3.2rem',
            color: v2Colors.text.secondary,
            fontStyle: 'oblique',
            margin: '0 1rem 2rem',
            [`@media (max-width: ${breakpoints.xl})`]: {
                fontSize: '2.8rem',
            },
            [`@media (max-width: ${breakpoints.lg})`]: {
                fontSize: '2.6rem',
            },
            [`@media (max-width: ${breakpoints.md})`]: {
                fontSize: '2.4rem',
            },
        },
        '& ul': {
            margin: '2rem 0 2.2rem',
            '& li::marker': {
                color: v2Colors.text.activePrimary,
            },
            '&:last-child': {
                marginBottom: 0,
            },
        },
        '& ol': {
            margin: '2rem 0 2.2rem',
            '&:last-child': {
                marginBottom: 0,
            },
        },
        '& li': {
            ...typographyStyles.body1,
            color: 'inherit',
            wordBreak: 'break-word',
            '&:not(:last-of-type)': {
                marginBottom: '1em',
            },
            '& ul,ol': {
                marginTop: '1em',
                marginBottom: 0,
            },
        },
        '& pre': {
            ...typographyStyles.body1,
            whiteSpace: 'pre-wrap',
            backgroundColor: v2Colors.core.cloud,
            fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
            color: v2Colors.text.secondary,
        },
        '& code': {
            ...typographyStyles.body1,
            padding: '2rem',
            color: v2Colors.text.secondary,
            wordBreak: 'break-word',
            fontFamily: '"source-code-pro", "Menlo", "Monaco", "Consolas", "Courier New", "monospace"',
        },
        '& table, td': {
            ...typographyStyles.body1,
            color: v2Colors.text.secondary,
            border: `1px solid ${'#BEC7CF'}`,
            borderCollapse: 'collapse',
        },
        '& table': {
            display: 'table',
            margin: 'auto',
            '& td': {
                padding: '0.72rem',
            },
        },
        '& img': {
            width: '100%',
            maxWidth: '100%',
            height: '100%',
        },
        '& figure': {
            display: 'block',
            width: 'auto',
            margin: 0,
            maxWidth: '100%',
            position: 'relative',
        },
        '& figure.media': {
            margin: 'auto',
            width: '100%',
            clear: 'both',
            '& > div > div': {
                paddingBottom: '56.25% !important',
            },
        },

        '& figure.image': {
            margin: '2rem 2rem 2rem 0',
            boxSizing: 'content-box !important' as 'content-box',
            '& a': {
                padding: 0,
            },
            '& a[target="_blank"]::after': {
                content: `url(${externalLinkWithBg})`,
                position: 'absolute',
                left: 0,
                bottom: 0,
            },
            '& a[target="_blank"]:hover::after': {
                content: `url(${externalLinkWithBg})`,
                position: 'absolute',
                left: 0,
                bottom: 0,
            },
            '& a[target="_blank"]:focus::after': {
                content: `url(${externalLinkWithBg})`,
                position: 'absolute',
                left: 0,
                bottom: 0,
            },
            '& a[target="_blank"]:hover::before': {
                content: 'linear-gradient(140deg, rgba(0, 120, 211, 0) 0%, rgba(0, 120, 211, 0.3) 100%)',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: '0.5rem',
                top: 0,
            },
            '& a[target="_blank"]:focus::before': {
                content: 'linear-gradient(140deg, rgba(0, 120, 211, 0) 0%, rgba(0, 120, 211, 0.5) 100%)',
                position: 'absolute',
                left: 0,
                right: 0,
            },
        },
        '& figure.image-style-align-left': {
            float: 'left',
            margin: '2rem 2rem 2rem 0',
        },
        '& figure.image-style-align-center': {
            display: 'block',
            margin: '2rem auto',
        },
        '& figure.image-style-align-right': {
            float: 'right',
            margin: '2rem 0 2rem 2rem',
        },
        '& h1': {
            ...typographyStyles.header1,
            marginBottom: '0.3em',
        },
        '& h2': {
            ...typographyStyles.header3,
            marginBottom: '1.6rem',
            marginTop: '0.8em',
        },
        '& h3': {
            ...typographyStyles.header4,
            color: v2Colors.text.activePrimary,
            marginBottom: '1.6rem',
            marginTop: '1em',
        },
        '& h4, h6': {
            ...typographyStyles.body1,
            color: v2Colors.text.secondary,
        },
        '& h5': {
            ...typographyStyles.body2,
            color: v2Colors.text.secondary,
        },
        '& p': {
            ...typographyStyles.body1,
            color: v2Colors.text.secondary,
            wordBreak: 'break-word',
        },
    },
} as const;
